/**
 * LineSafetyFilters - Safety net utilities for cleaning snapshot lines
 *
 * Extracted from EdgeDocsCanvas.V3.tsx as part of Phase 3 Step 5.
 *
 * These filters handle edge cases where cached snapshot data becomes stale
 * due to document edits. They provide a deterministic, CRDT-safe way to
 * clean up line data before rendering.
 *
 * Safety Phases:
 * - Phase 0: Filter stale lines (end > document length)
 * - Phase 1: Sort lines by grapheme index
 * - Phase 2: Remove overlapping lines
 * - Phase 3: Remove exact duplicates
 */

/**
 * Base interface for line data - requires grapheme indices
 * Functions are generic to preserve the exact type passed in
 */
export interface LineWithGraphemeIndices {
  start_grapheme_index: number;
  end_grapheme_index: number;
  baseline_y_fp?: number;
}

/**
 * Result of applying safety filters to lines
 */
export interface SafetyFilterResult<T extends LineWithGraphemeIndices> {
  /** Cleaned lines after all filters applied */
  lines: T[];
  /** Number of stale lines filtered (Phase 0) */
  staleFiltered: number;
  /** Number of overlapping lines removed (Phase 2) */
  overlapsRemoved: number;
  /** Number of duplicate lines removed (Phase 3) */
  duplicatesRemoved: number;
}

/**
 * Options for safety filtering
 */
export interface SafetyFilterOptions {
  /** Current committed document length for stale detection */
  committedLength: number;
  /** Page index for logging context */
  pageIndex?: number;
  /** Enable verbose console logging */
  enableLogging?: boolean;
}

/**
 * Phase 0: Filter stale lines beyond committed document length
 *
 * When DELETE operations shrink the document, cached snapshots may contain
 * lines with grapheme indices beyond the current document length.
 *
 * @param lines - Lines to filter
 * @param committedLength - Current document length
 * @param enableLogging - Log filtered lines
 * @returns Filtered lines and count of removed lines
 */
export function filterStaleLines<T extends LineWithGraphemeIndices>(
  lines: T[],
  committedLength: number,
  enableLogging: boolean = false
): { lines: T[]; staleFiltered: number } {
  const result: T[] = [];
  let staleFiltered = 0;

  for (const line of lines) {
    if (line.end_grapheme_index > committedLength) {
      if (enableLogging) {
        console.warn(`🗑️ [LineSafetyFilters] Filtering stale line [${line.start_grapheme_index}-${line.end_grapheme_index}) > committedLen=${committedLength}`);
      }
      staleFiltered++;
    } else {
      result.push(line);
    }
  }

  if (staleFiltered > 0 && enableLogging) {
    console.log(`🛡️ [LineSafetyFilters] PHASE 0: Filtered ${staleFiltered} stale lines (end > committedLen=${committedLength})`);
  }

  return { lines: result, staleFiltered };
}

/**
 * Phase 1: Sort lines by start_grapheme_index
 *
 * Ensures deterministic ordering for subsequent processing.
 *
 * @param lines - Lines to sort (mutates array in place for efficiency)
 */
export function sortLinesByGraphemeIndex<T extends LineWithGraphemeIndices>(lines: T[]): void {
  lines.sort((a, b) => a.start_grapheme_index - b.start_grapheme_index);
}

/**
 * Phase 2: Remove overlapping lines
 *
 * Overlapping = line N's start < line N-1's end
 * Root cause: stale snapshots with incorrect grapheme indices after edits
 * Strategy: Keep the LARGER line when overlap detected (smaller is likely stale)
 *
 * This is DETERMINISTIC (same input → same output) and CRDT-safe (local render only)
 *
 * @param lines - Pre-sorted lines to process
 * @param enableLogging - Log overlap decisions
 * @returns Cleaned lines and count of removed overlaps
 */
export function removeOverlappingLines<T extends LineWithGraphemeIndices>(
  lines: T[],
  enableLogging: boolean = false
): { lines: T[]; overlapsRemoved: number } {
  const cleanedLines: T[] = [];
  let overlapsRemoved = 0;

  for (const line of lines) {
    if (cleanedLines.length === 0) {
      cleanedLines.push(line);
      continue;
    }

    const prevLine = cleanedLines[cleanedLines.length - 1];
    const prevEnd = prevLine.end_grapheme_index;
    const thisStart = line.start_grapheme_index;

    // Check for overlap: this line starts before previous line ends
    if (thisStart < prevEnd) {
      // Overlap detected - decide which to keep
      const prevSize = prevLine.end_grapheme_index - prevLine.start_grapheme_index;
      const thisSize = line.end_grapheme_index - line.start_grapheme_index;

      if (thisSize > prevSize) {
        // This line is larger - replace previous (it's likely stale)
        if (enableLogging) {
          console.warn(`🔬 [LineSafetyFilters:OVERLAP] Replacing smaller line with larger:`, {
            removed: `[${prevLine.start_grapheme_index}-${prevLine.end_grapheme_index})`,
            kept: `[${line.start_grapheme_index}-${line.end_grapheme_index})`,
            removedY: Math.round((prevLine.baseline_y_fp || 0) / 64),
            keptY: Math.round((line.baseline_y_fp || 0) / 64),
          });
        }
        cleanedLines[cleanedLines.length - 1] = line;
      } else {
        // Previous line is larger or equal - skip this line (it's likely stale)
        if (enableLogging) {
          console.warn(`🔬 [LineSafetyFilters:OVERLAP] Removing overlapping line:`, {
            removed: `[${line.start_grapheme_index}-${line.end_grapheme_index})`,
            keptPrev: `[${prevLine.start_grapheme_index}-${prevLine.end_grapheme_index})`,
            removedY: Math.round((line.baseline_y_fp || 0) / 64),
          });
        }
      }
      overlapsRemoved++;
    } else {
      // No overlap - add this line
      cleanedLines.push(line);
    }
  }

  return { lines: cleanedLines, overlapsRemoved };
}

/**
 * Phase 3: Remove exact duplicates (same start-end range)
 *
 * ✅ FIX (2026-01-16): Skip zero-length lines (MS Word model)
 * Zero-length lines represent empty paragraphs from pressing ENTER multiple times.
 * Multiple zero-length lines at the same grapheme position but different Y coordinates
 * are NOT duplicates - they represent intentional vertical spacing.
 *
 * @param lines - Lines to deduplicate
 * @param enableLogging - Log duplicate removals
 * @returns Deduplicated lines and count of removed duplicates
 */
export function removeDuplicateLines<T extends LineWithGraphemeIndices>(
  lines: T[],
  enableLogging: boolean = false
): { lines: T[]; duplicatesRemoved: number } {
  const seenRanges = new Map<string, number>();
  const result: T[] = [];
  let duplicatesRemoved = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineLength = line.end_grapheme_index - line.start_grapheme_index;

    // Skip deduplication for zero-length lines (empty paragraphs)
    // These represent vertical spacing and can legitimately have the same range
    if (lineLength === 0) {
      result.push(line);
      continue;
    }

    const key = `${line.start_grapheme_index}-${line.end_grapheme_index}`;

    if (seenRanges.has(key)) {
      if (enableLogging) {
        console.warn(`🔬 [LineSafetyFilters:DEDUP] Removing duplicate line at index ${i}:`, {
          range: key,
          firstOccurrence: seenRanges.get(key),
          thisY: Math.round((line.baseline_y_fp || 0) / 64),
        });
      }
      duplicatesRemoved++;
    } else {
      seenRanges.set(key, i);
      result.push(line);
    }
  }

  return { lines: result, duplicatesRemoved };
}

/**
 * Apply all safety filters to snapshot lines
 *
 * Combines all four safety phases into a single operation:
 * 1. Filter stale lines (end > document length)
 * 2. Sort by grapheme index
 * 3. Remove overlapping lines
 * 4. Remove exact duplicates
 *
 * @param lines - Raw lines from snapshot
 * @param options - Filter options
 * @returns Cleaned lines with statistics
 *
 * @example
 * ```typescript
 * const result = applyLineSafetyFilters(pageLines, {
 *   committedLength: currentDocumentTextRef.current?.length ?? 0,
 *   pageIndex: 0,
 *   enableLogging: true
 * });
 *
 * console.log(`Cleaned ${result.lines.length} lines`);
 * ```
 */
export function applyLineSafetyFilters<T extends LineWithGraphemeIndices>(
  lines: T[],
  options: SafetyFilterOptions
): SafetyFilterResult<T> {
  const { committedLength, enableLogging = false } = options;

  // Phase 0: Filter stale lines
  const phase0 = filterStaleLines(lines, committedLength, enableLogging);

  // Phase 1: Sort by grapheme index (mutates in place)
  sortLinesByGraphemeIndex(phase0.lines);

  // Phase 2: Remove overlapping lines
  const phase2 = removeOverlappingLines(phase0.lines, enableLogging);

  // Phase 3: Remove duplicates
  const phase3 = removeDuplicateLines(phase2.lines, enableLogging);

  return {
    lines: phase3.lines,
    staleFiltered: phase0.staleFiltered,
    overlapsRemoved: phase2.overlapsRemoved,
    duplicatesRemoved: phase3.duplicatesRemoved
  };
}
